import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Game } from './engine/Game';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../utils/apiConfig';
import { CAR_CATALOG, CAR_STARTER_ID, getCarTier } from './game/cars/CarCatalog';
import { useGaragePersistence } from './engine/GaragePersistence';
import { getUnlockedCars, setUnlockedCars as storeUnlockedCars, addMoney, addXP, getSponsor, setSponsor } from './engine/CarUnlockStore';
import type { CarPreset, CarTuning } from './game/cars/CarTypes';
import { computeRaceRewards } from './game/race/RaceRewards';
import { updateContractProgress } from './engine/SponsorSystem';
import CarRaceGameView from './CarRaceGameView';

type TuningState = CarTuning;

type CarRaceGameProps = {
  initialView?: 'menu' | 'garage' | 'race' | 'free';
};

const CarRaceGame: React.FC<CarRaceGameProps> = ({ initialView = 'menu' }) => {
  const { token, user, totalScore } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const minimapRef = useRef<HTMLCanvasElement | null>(null);
  const gameRef = useRef<Game | null>(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [paused, setPaused] = useState(false);
  const [fps, setFps] = useState(0);
  const [garageOpen, setGarageOpen] = useState(initialView === 'garage');
  const [status, setStatus] = useState<ReturnType<Game['getUiState']> | null>(null);
  const lastUiUpdateRef = useRef(0);
  const [tuningByCarId, setTuningByCarId] = useState<Record<string, TuningState>>({});
  const [presetsByCarId, setPresetsByCarId] = useState<Record<string, CarPreset[]>>({});
  const [presetName, setPresetName] = useState('');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [ghostEnabled, setGhostEnabled] = useState(true);
  const [raceSetupOpen, setRaceSetupOpen] = useState(initialView === 'menu');
  const [activeView, setActiveView] = useState<'menu' | 'garage' | 'race' | 'free'>(initialView);
  const [freeRideMapId, setFreeRideMapId] = useState<string>('bw_openworld');
  const [touchControlsEnabled, setTouchControlsEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('carRace_touchControlsEnabled');
      return stored ? stored === '1' : false;
    } catch {
      return false;
    }
  });
  const raceRewardLatchRef = useRef<boolean>(false);
  
  // Mobile Detection (für Joystick)
  const [isMobile] = useState(() => {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  });

  const canViewDebug = useMemo(() => {
    return user?.permissions?.includes('can_view_car_race_debug') ?? false;
  }, [user?.permissions]);

  useEffect(() => {
    if (!mountRef.current) return;

    const game = new Game(mountRef.current, (nextStatus) => {
      const now = performance.now();
      if (now - lastUiUpdateRef.current < 120) return;
      lastUiUpdateRef.current = now;
      setFps(nextStatus.fps);
      setStatus(nextStatus.uiState);
    }, { showDebugHud: canViewDebug });

    game.start();
    gameRef.current = game;
    game.setOpenWorldMode(initialView === 'free');

    return () => {
      game.stop();
      gameRef.current = null;
    };
  }, [initialView, canViewDebug]);

  useEffect(() => {
    if (!token) return;

    const fetchTuning = async () => {
      try {
        const response = await fetch(`${apiConfig.apiUrl}?action=getCarTuning`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) return;
        const tuning = data.tuning || {};
        const nextTuning: Record<string, TuningState> = {};
        const nextPresets: Record<string, CarPreset[]> = {};

        Object.entries(tuning).forEach(([carId, values]) => {
          if (values && typeof values === 'object') {
            const asAny = values as { current?: TuningState; presets?: CarPreset[] } & TuningState;
            if (asAny.current || asAny.presets) {
              if (asAny.current) nextTuning[carId] = asAny.current;
              if (Array.isArray(asAny.presets)) nextPresets[carId] = asAny.presets;
            } else {
              nextTuning[carId] = asAny;
            }
          }
        });

        setTuningByCarId(nextTuning);
        setPresetsByCarId(nextPresets);
        Object.entries(nextTuning).forEach(([carId, values]) => {
          gameRef.current?.setCarTuning(carId, values as any);
        });
      } catch {
        // ignore
      }
    };

    fetchTuning();
  }, [token]);

  const garageMgr = useGaragePersistence(user?.id ?? '', token ?? '');

  useEffect(() => {
    if (!user?.id || !token) return;

    const loadGarage = async () => {
      try {
        await garageMgr.loadGarage();

        const stored = getUnlockedCars(user.id);
        const ensured = stored.includes(CAR_STARTER_ID) ? stored : [CAR_STARTER_ID, ...stored];
        storeUnlockedCars(user.id, ensured);

        if (totalScore > 0) {
          await garageMgr.syncPoints(totalScore);
        }
      } catch {
        // ignore
      }
    };

    loadGarage();
  }, [garageMgr, token, totalScore, user?.id]);

  useEffect(() => {
    if (!status) return;
    if (status.ghostEnabled !== ghostEnabled) {
      setGhostEnabled(status.ghostEnabled);
    }
  }, [status, ghostEnabled]);

  useEffect(() => {
    if (!gameRef.current) return;
    gameRef.current.setOpenWorldMode(activeView === 'free' && freeRideMapId === 'bw_openworld');
  }, [activeView, freeRideMapId]);

  useEffect(() => {
    try {
      localStorage.setItem('carRace_touchControlsEnabled', touchControlsEnabled ? '1' : '0');
    } catch {
      // ignore
    }
  }, [touchControlsEnabled]);

  useEffect(() => {
    if (!user?.id || !status) return;

    const completed = Boolean((status as any).raceCompleted);
    if (completed && !raceRewardLatchRef.current) {
      raceRewardLatchRef.current = true;

      const place = (status as any).playerPlace ? Number((status as any).playerPlace) : 1;
      const timeSec = Number(status.raceTime || 0);
      const driftScore = Number((status as any).driftScore || 0);
      const heatLevel = Number((status as any).heatLevel || 0);
      const districtTier = (status as any).districtTier
        ? (status as any).districtTier
        : (getCarTier(status.carId) as 1 | 2 | 3);

      const sponsor = getSponsor(user.id);
      const rewards = computeRaceRewards({
        mode: 'vs_rivals',
        place,
        timeSec,
        driftScore,
        heatLevel,
        districtTier,
        sponsor
      });

      addMoney(user.id, rewards.money);
      addXP(user.id, rewards.xp);

      const sp = getSponsor(user.id);
      if (sp) {
        const upd = updateContractProgress(sp, { driftScore, heatLevel });
        setSponsor(user.id, upd.contract);
        if (upd.completedNow) {
          addMoney(user.id, upd.contract.moneyBonus);
        }
      }
    }

    if (!completed) {
      raceRewardLatchRef.current = false;
    }
  }, [status, user?.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'g') {
        setGarageOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!status || !minimapRef.current) return;

    const canvas = minimapRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { mapBounds, position, checkpointTarget } = status;
    const size = canvas.width;
    const halfW = mapBounds.width / 2;
    const halfH = mapBounds.height / 2;

    ctx.clearRect(0, 0, size, size);
    ctx.fillStyle = 'rgba(10, 15, 22, 0.85)';
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.strokeRect(1, 1, size - 2, size - 2);

    const worldToMap = (x: number, z: number) => {
      const nx = (x + halfW) / mapBounds.width;
      const nz = (z + halfH) / mapBounds.height;
      return { x: nx * size, y: (1 - nz) * size };
    };

    if (checkpointTarget) {
      const target = worldToMap(checkpointTarget.x, checkpointTarget.z);
      ctx.fillStyle = 'rgba(46,230,255,0.8)';
      ctx.beginPath();
      ctx.arc(target.x, target.y, 6, 0, Math.PI * 2);
      ctx.fill();
    }

    const car = worldToMap(position.x, position.z);
    ctx.fillStyle = '#ffca40';
    ctx.beginPath();
    ctx.arc(car.x, car.y, 4, 0, Math.PI * 2);
    ctx.fill();
  }, [status]);

  const selectedCarId = status?.carId || CAR_CATALOG[0].id;
  const selectedCar = useMemo(
    () => CAR_CATALOG.find((car) => car.id === selectedCarId) || CAR_CATALOG[0],
    [selectedCarId]
  );
  const currentTuning = tuningByCarId[selectedCar.id] || {};

  const applyTuning = (next: TuningState) => {
    setTuningByCarId((prev) => ({ ...prev, [selectedCar.id]: next }));
    gameRef.current?.setCarTuning(selectedCar.id, next as any);
  };

  const handleSaveTuning = async () => {
    if (!token) {
      setSaveMessage('Nicht eingeloggt');
      return;
    }
    setSaving(true);
    setSaveMessage(null);
    try {
      const response = await fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'updateCarTuning',
          carId: selectedCar.id,
          tuning: {
            current: currentTuning,
            presets: presetsByCarId[selectedCar.id] || []
          }
        })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success) {
        setSaveMessage(data?.error || 'Speichern fehlgeschlagen');
      } else {
        setSaveMessage('Gespeichert');
      }
    } catch {
      setSaveMessage('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handlePauseToggle = () => {
    const nextPaused = !paused;
    setPaused(nextPaused);
    gameRef.current?.setPaused(nextPaused);
  };

  const handleReset = () => {
    setPaused(false);
    gameRef.current?.reset();
  };

  const handleRaceStart = () => {
    setActiveView('race');
    gameRef.current?.setOpenWorldMode(false);
    gameRef.current?.startRace();
  };

  const applyMode = (next: 'menu' | 'garage' | 'race' | 'free') => {
    setActiveView(next);
    setGarageOpen(next === 'garage');
    if (next === 'menu') {
      setRaceSetupOpen(true);
      gameRef.current?.setOpenWorldMode(false);
      return;
    }
    if (next === 'race') {
      gameRef.current?.setOpenWorldMode(false);
      return;
    }
    if (next === 'free') {
      setGarageOpen(false);
      if (freeRideMapId === 'bw_openworld') {
        gameRef.current?.setOpenWorldMode(true);
      } else {
        gameRef.current?.setOpenWorldMode(false);
        gameRef.current?.setUrbanTrack(freeRideMapId);
      }
    }
  };

  const handleModeSwitch = (_event: React.MouseEvent<HTMLElement>, next: 'menu' | 'garage' | 'race' | 'free' | null) => {
    if (!next) return;
    applyMode(next);
  };

  const handleStartRaceSetup = (carId: string, trackId: string) => {
    gameRef.current?.setCarById(carId);
    gameRef.current?.setUrbanTrack(trackId);
    gameRef.current?.setOpenWorldMode(false);
    gameRef.current?.startRace();
    setGarageOpen(false);
    setRaceSetupOpen(false);
    setActiveView('race');
  };

  const handleGhostToggle = () => {
    const next = !ghostEnabled;
    setGhostEnabled(next);
    gameRef.current?.setGhostEnabled(next);
  };

  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      // Fullscreen aktivieren
      if (containerRef.current?.requestFullscreen) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      // Fullscreen deaktivieren
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  // Monitor fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const createTouchHandlers = (keyCode: string) => ({
    onPointerDown: () => gameRef.current?.setVirtualKey(keyCode, true),
    onPointerUp: () => gameRef.current?.setVirtualKey(keyCode, false),
    onPointerLeave: () => gameRef.current?.setVirtualKey(keyCode, false),
    onPointerCancel: () => gameRef.current?.setVirtualKey(keyCode, false)
  });
  
  // Virtual Joystick Handler (Multi-Touch Support)
  const handleJoystickChange = (state: { x: number; y: number; active: boolean }) => {
    if (gameRef.current) {
      gameRef.current.setVirtualJoystick(state.x, state.y, state.active);
    }
  };

  const sliderConfig = useMemo(() => {
    const base = selectedCar;
    return {
      accel: { min: base.accel * 0.7, max: base.accel * 1.3, step: 0.5 },
      brake: { min: base.brake * 0.7, max: base.brake * 1.3, step: 0.5 },
      maxSpeed: { min: base.maxSpeed * 0.8, max: base.maxSpeed * 1.3, step: 1 },
      handling: { min: base.handling * 0.7, max: base.handling * 1.3, step: 0.05 },
      grip: { min: base.grip * 0.7, max: base.grip * 1.3, step: 0.1 },
      driftGrip: { min: base.driftGrip * 0.6, max: base.driftGrip * 1.3, step: 0.1 },
      drag: { min: base.drag * 0.7, max: base.drag * 1.3, step: 0.02 }
    };
  }, [selectedCar]);

  const colorValue = currentTuning.colorHex ?? '#1580ff';
  const neonColorValue = currentTuning.neonColorHex ?? '#2ee6ff';
  const neonIntensityValue = currentTuning.neonIntensity ?? 0.18;

  const handleSlider = (key: keyof TuningState, value: number) => {
    applyTuning({ ...currentTuning, [key]: value });
  };

  const resetTuning = () => {
    applyTuning({});
  };

  const handleSavePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const entry: CarPreset = {
      id: `${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name,
      tuning: { ...currentTuning }
    };
    setPresetsByCarId((prev) => ({
      ...prev,
      [selectedCar.id]: [...(prev[selectedCar.id] || []), entry]
    }));
    setPresetName('');
  };

  const handleApplyPreset = (preset: CarPreset) => {
    applyTuning({ ...preset.tuning });
  };

  const handleDeletePreset = (presetId: string) => {
    setPresetsByCarId((prev) => ({
      ...prev,
      [selectedCar.id]: (prev[selectedCar.id] || []).filter((preset) => preset.id !== presetId)
    }));
  };

  const handleOpenGarageFromSetup = () => {
    setRaceSetupOpen(false);
    setGarageOpen(true);
    setActiveView('garage');
  };

  const applyFreeRideMapChange = useCallback(() => {
    if (!gameRef.current) return;
    if (freeRideMapId === 'bw_openworld') {
      gameRef.current.setOpenWorldMode(true);
    } else {
      gameRef.current.setOpenWorldMode(false);
      gameRef.current.setUrbanTrack(freeRideMapId);
    }
  }, [freeRideMapId]);

  const handleStartFreeRide = () => {
    if (freeRideMapId !== 'bw_openworld') {
      applyFreeRideMapChange();
      setGarageOpen(false);
      setActiveView('free');
      return;
    }
    applyMode('free');
  };

  useEffect(() => {
    if (activeView !== 'free') return;
    applyFreeRideMapChange();
  }, [activeView, applyFreeRideMapChange]);

  return React.createElement(CarRaceGameView, {
    containerRef,
    mountRef,
    minimapRef,
    isFullscreen,
    fps,
    selectedCar,
    activeView,
    onModeSwitch: handleModeSwitch,
    onOpenRaceSetup: () => setRaceSetupOpen(true),
    onStartFreeRide: handleStartFreeRide,
    onToggleFullscreen: toggleFullscreen,
    status,
    paused,
    ghostEnabled,
    onPauseToggle: handlePauseToggle,
    onReset: handleReset,
    onRaceStart: handleRaceStart,
    onGhostToggle: handleGhostToggle,
    onToggleGarage: () => setGarageOpen((prev) => !prev),
    garageOpen,
    sliderConfig,
    currentTuning,
    onSliderChange: handleSlider,
    colorValue,
    neonColorValue,
    neonIntensityValue,
    onApplyTuning: applyTuning,
    presetName,
    onPresetNameChange: setPresetName,
    onSavePreset: handleSavePreset,
    presetsByCarId,
    onApplyPreset: handleApplyPreset,
    onDeletePreset: handleDeletePreset,
    onResetTuning: resetTuning,
    onSaveTuning: handleSaveTuning,
    saving,
    saveMessage,
    createTouchHandlers,
    raceSetupOpen,
    onCloseRaceSetup: () => setRaceSetupOpen(false),
    onStartRaceSetup: handleStartRaceSetup,
    onOpenGarageFromSetup: handleOpenGarageFromSetup,
    isMobile,
    onJoystickChange: handleJoystickChange,
    freeRideMapId,
    onFreeRideMapChange: setFreeRideMapId,
    touchControlsEnabled,
    onToggleTouchControls: () => setTouchControlsEnabled((prev) => !prev)
  });
};

export default CarRaceGame;

